import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@chakra-ui/react'
import {HeroBannerV2} from '../hero-banner-v2'
import {remCalc} from '../../../theme/_utils'

const SLIDE_DURATION = 5000

export const HeroBannerCarouselV2 = ({slides = [], isFirstElement = false}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [progress, setProgress] = useState(0)
    const timerRef = useRef(null)
    const animFrameRef = useRef(null)
    const startTimeRef = useRef(null)
    const pointerStartXRef = useRef(null)
    const pointerStartYRef = useRef(null)
    const pointerCurrentXRef = useRef(null)
    const pointerCurrentYRef = useRef(null)
    const pointerIdRef = useRef(null)
    const touchStartXRef = useRef(null)
    const touchStartYRef = useRef(null)
    const lastSwipeAtRef = useRef(0)

    const SWIPE_THRESHOLD = 40
    const SWIPE_CLICK_SUPPRESSION_MS = 300
    const slideOffset = `translate3d(-${activeIndex * 100}%, 0, 0)`

    const resetAutoPlay = () => {
        cancelAnimationFrame(animFrameRef.current)
        clearTimeout(timerRef.current)
    }

    const clearPointerState = () => {
        pointerStartXRef.current = null
        pointerStartYRef.current = null
        pointerCurrentXRef.current = null
        pointerCurrentYRef.current = null
        pointerIdRef.current = null
    }

    const goToSlide = useCallback(
        (index) => {
            const clamped = Math.min(Math.max(index, 0), slides.length - 1)
            setActiveIndex(clamped)
            setProgress(0)
            startTimeRef.current = null
        },
        [slides.length],
    )

    const goNext = useCallback(() => {
        setActiveIndex((prev) => {
            const next = prev < slides.length - 1 ? prev + 1 : 0
            setProgress(0)
            startTimeRef.current = null
            return next
        })
    }, [slides.length])

    const handleSwipeByDelta = (deltaX, deltaY) => {
        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return false
        lastSwipeAtRef.current = Date.now()
        resetAutoPlay()
        if (deltaX < 0) {
            goNext()
            return true
        }
        goToSlide(activeIndex - 1)
        return true
    }

    useEffect(() => {
        if (!slides.length) return

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp
            const elapsed = timestamp - startTimeRef.current
            const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100)
            setProgress(pct)
            if (pct < 100) {
                animFrameRef.current = requestAnimationFrame(animate)
            } else {
                timerRef.current = setTimeout(() => {
                    setActiveIndex((prev) => {
                        const next = prev < slides.length - 1 ? prev + 1 : 0
                        setProgress(0)
                        startTimeRef.current = null
                        return next
                    })
                }, 0)
            }
        }

        animFrameRef.current = requestAnimationFrame(animate)
        return () => {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
        }
    }, [activeIndex, slides.length])

    const handleClick = () => {
        if (Date.now() - lastSwipeAtRef.current < SWIPE_CLICK_SUPPRESSION_MS) {
            lastSwipeAtRef.current = 0
            return
        }
        resetAutoPlay()
        goNext()
    }

    const handlePointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return
        pointerIdRef.current = e.pointerId
        pointerStartXRef.current = e.clientX
        pointerStartYRef.current = e.clientY
        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY
        if (e.currentTarget?.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e) => {
        if (pointerIdRef.current !== e.pointerId) return
        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY
    }

    const handlePointerUp = (e) => {
        if (pointerIdRef.current !== e.pointerId) return
        if (pointerStartXRef.current === null || pointerStartYRef.current === null) {
            pointerIdRef.current = null
            return
        }
        const endX = pointerCurrentXRef.current ?? e.clientX
        const endY = pointerCurrentYRef.current ?? e.clientY
        const deltaX = endX - pointerStartXRef.current
        const deltaY = endY - pointerStartYRef.current
        clearPointerState()
        if (e.currentTarget?.releasePointerCapture) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId)
            } catch (_) {}
        }
        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleTouchStart = (e) => {
        const touch = e.touches?.[0]
        if (!touch) return
        touchStartXRef.current = touch.clientX
        touchStartYRef.current = touch.clientY
    }

    const handleTouchEnd = (e) => {
        const touch = e.changedTouches?.[0]
        if (!touch || touchStartXRef.current === null || touchStartYRef.current === null) return
        const deltaX = touch.clientX - touchStartXRef.current
        const deltaY = touch.clientY - touchStartYRef.current
        touchStartXRef.current = null
        touchStartYRef.current = null
        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleTouchCancel = () => {
        touchStartXRef.current = null
        touchStartYRef.current = null
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
        }
        if (e.key === 'ArrowRight') {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
            goNext()
        }
        if (e.key === 'ArrowLeft') {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
            goToSlide(activeIndex - 1)
        }
    }

    if (!slides.length) return null

    return (
        <Box
            position="relative"
            overflow="hidden"
            w="100%"
            maxW={remCalc(1920)}
            mx="auto"
            data-testid="hero-banner-carousel-v2"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={clearPointerState}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            role="group"
            tabIndex={0}
            aria-label={`Hero banner slide ${activeIndex + 1} of ${slides.length}`}
        >
            <Flex style={{transform: slideOffset, transition: 'transform 0.4s ease'}}>
                {slides.map((slide, idx) => {
                    const textBlock = {
                        label: slide?.textblock?.label,
                        subtitle: slide?.textblock?.subtitle,
                        title: slide?.textblock?.title,
                        bodyCopy: slide?.textblock?.bodycopy,
                        desktopAlignment: slide?.textblock?.desktopalignment,
                        tabletMobileAlignment: slide?.textblock?.tabletmobilealignment,
                    }
                    return (
                        <Box key={idx} minW="100%" w="100%" flexShrink={0}>
                            <HeroBannerV2
                                imageSet={slide?.image}
                                textBlock={textBlock}
                                isMaskHeaderActive={!!slide?.maskgroup?.maskheaderactive}
                                variant={slide?.backgroundcolor}
                                buttonText={slide?.button}
                                buttonText2={slide?.second_button}
                                countdown={slide?.countdown?.length ? slide.countdown : undefined}
                                videos={slide?.videos}
                                isFirstElement={isFirstElement && idx === 0}
                                hasPaddings={false}
                                isCarouselV2
                            />
                        </Box>
                    )
                })}
            </Flex>

            {/* Progress bar — only shown when there are multiple slides */}
            {slides.length > 1 && (
                <Flex
                    display={{base: 'none', md: 'flex'}}
                    position="absolute"
                    bottom={3}
                    left={4}
                    right={4}
                    gap={2}
                    zIndex={10}
                    pointerEvents="none"
                    aria-hidden="true"
                >
                    {slides.map((_, idx) => {
                        const barWidth =
                            idx < activeIndex ? '100%' : idx === activeIndex ? `${progress}%` : '0%'
                        return (
                            <Box
                                key={idx}
                                flex={1}
                                h={remCalc(2)}
                                bg="neutral.alphaWhite.100"
                                borderRadius="full"
                                overflow="hidden"
                            >
                                <Box
                                    h="100%"
                                    bg="neutral.alphaWhite.950"
                                    style={{width: barWidth, transition: 'width 0.05s linear'}}
                                />
                            </Box>
                        )
                    })}
                </Flex>
            )}
        </Box>
    )
}

HeroBannerCarouselV2.propTypes = {
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.object,
            textblock: PropTypes.object,
            maskgroup: PropTypes.object,
            button: PropTypes.object,
            second_button: PropTypes.object,
            countdown: PropTypes.array,
            videos: PropTypes.object,
            backgroundcolor: PropTypes.string,
        }),
    ),
    isFirstElement: PropTypes.bool,
}
